class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');
    this.searchResultsWidget = this.getElementsByClassName('quickSearchResultsWidget')[0];

    // Grab trending HTML before we do anything
    this.trendingHTML = '';
    if (this.trendingAndProductsBlock) {
      const inner = this.trendingAndProductsBlock.querySelector('.quickSearchResults');
      if (inner) this.trendingHTML = inner.innerHTML;
    }

    // Build the search modal (appended to body, outside header DOM)
    this.buildSearchModal();
    this.setupEventListeners();
  }

  buildSearchModal() {
    // Create modal overlay + panel
    this.modal = document.createElement('div');
    this.modal.className = 'search-modal-overlay';
    this.modal.style.display = 'none';
    this.modal.innerHTML = `
      <div class="search-modal-panel">
        <div class="search-modal-panel__header">
          <div class="search-modal-panel__search-bar">
            <form action="${window.routes?.search_url || '/search'}" method="get" role="search" class="search-modal-panel__form">
              <svg class="search-modal-panel__icon" role="img" viewBox="0 0 512 512"><path d="M495,466.2L377.2,348.4c29.2-35.6,46.8-81.2,46.8-130.9C424,103.5,331.5,11,217.5,11C103.4,11,11,103.5,11,217.5S103.4,424,217.5,424c49.7,0,95.2-17.5,130.8-46.7L466.1,495c8,8,20.9,8,28.9,0C503,487.1,503,474.1,495,466.2z M217.5,382.9C126.2,382.9,52,308.7,52,217.5S126.2,52,217.5,52C308.7,52,383,126.3,383,217.5S308.7,382.9,217.5,382.9z"></path></svg>
              <input class="search-modal-panel__input" type="search" name="q" placeholder="${this.input?.placeholder || 'Search...'}" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false">
              <input type="hidden" name="options[prefix]" value="last">
              <input type="hidden" name="type" value="product">
              <button type="button" class="search-modal-panel__close" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </form>
          </div>
        </div>
        <div class="search-modal-panel__body">
          <div class="search-modal-panel__results"></div>
        </div>
      </div>
    `;
    document.body.appendChild(this.modal);

    // Cache modal elements
    this.modalInput = this.modal.querySelector('.search-modal-panel__input');
    this.modalResults = this.modal.querySelector('.search-modal-panel__results');
    this.modalCloseBtn = this.modal.querySelector('.search-modal-panel__close');
    this.modalOverlay = this.modal;
    this.modalPanel = this.modal.querySelector('.search-modal-panel');

    // Modal event listeners — the X only clears the current search (digitec-style);
    // it closes the modal only when the field is already empty
    this.modalCloseBtn.addEventListener('click', () => {
      if (this.modalInput.value.trim().length) {
        this.clearSearch();
      } else {
        this.closeModal();
      }
    });
    this.modalOverlay.addEventListener('click', (e) => {
      if (e.target === this.modalOverlay) this.closeModal();
    });

    this.modalInput.addEventListener('input', debounce(() => {
      this.onModalInput();
    }, 300));

    this.modalInput.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') this.closeModal();
    });

    this.modal.querySelector('.search-modal-panel__form').addEventListener('submit', (e) => {
      const q = this.modalInput.value.trim();
      if (!q.length) e.preventDefault();
    });
  }

  setupEventListeners() {
    // Intercept clicks on the header search input to open our modal instead
    this.input.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openModal();
    });

    this.input.addEventListener('focus', (e) => {
      e.preventDefault();
      this.input.blur();
      this.openModal();
    });

    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }

    // Also intercept the summary click to prevent the details-modal from opening
    const summary = this.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      });
    }
  }

  openModal() {
    if (this.modal.style.display === 'flex') return;

    // Show trending content by default
    if (this.trendingHTML) {
      this.modalResults.innerHTML = this.trendingHTML;
    }

    // Lock scroll — save position first
    this.savedScrollY = window.scrollY;
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.classList.add('search-popup-open');
    document.documentElement.classList.add('search-popup-open');

    this.modal.style.display = 'flex';

    // Focus the modal search input
    setTimeout(() => {
      this.modalInput.focus();
    }, 50);

    // Close any open details
    const details = this.querySelector('details[open]');
    if (details) details.removeAttribute('open');
  }

  clearSearch() {
    this.abortController.abort();
    this.abortController = new AbortController();

    this.modalInput.value = '';
    this.searchTerm = '';
    this.modalResults.classList.remove('is-loading');

    // Back to the trending/default state, keep the search open and focused
    if (this.trendingHTML) {
      this.modalResults.innerHTML = this.trendingHTML;
    } else {
      this.modalResults.innerHTML = '';
    }
    this.modalInput.focus();
  }

  closeModal() {
    this.modal.style.display = 'none';

    // Restore scroll position
    document.body.classList.remove('search-popup-open');
    document.documentElement.classList.remove('search-popup-open');
    document.body.style.top = '';
    window.scrollTo(0, this.savedScrollY || 0);

    this.modalInput.value = '';
    this.searchTerm = '';
    this.isOpen = false;

    // Show trending again for next open
    if (this.trendingHTML) {
      this.modalResults.innerHTML = this.trendingHTML;
    }
  }

  onModalInput() {
    const searchTerm = this.modalInput.value.trim();

    if (!searchTerm.length) {
      // Show trending when input is cleared
      if (this.trendingHTML) {
        this.modalResults.innerHTML = this.trendingHTML;
      }
      this.searchTerm = '';
      return;
    }

    this.searchTerm = searchTerm;
    this.getSearchResults(searchTerm);
  }

  // --- Search API ---

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    // Show loading
    this.modalResults.classList.add('is-loading');

    if (this.cachedResults[queryKey]) {
      this.renderModalResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const sectionFetch = fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,product,collection,page,article`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      });

    // Total product count for the "view all" label — same query params as the view-all link target
    const countFetch = fetch(`${window.routes?.search_url || '/search'}?view=ajax_quick_search&q=${encodeURIComponent(searchTerm)}&type=product&options%5Bprefix%5D=last`, {
      signal: this.abortController.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    Promise.all([sectionFetch, countFetch])
      .then(([text, countData]) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');

        // Liquid can't know the total count in the predictive search context, so it
        // renders an empty "()" — fill it from the ajax_quick_search response.
        const viewAllLink = doc.querySelector('[data-qs-view-all-link]');
        if (viewAllLink) {
          const viewAllLabel = viewAllLink.querySelector('[data-qs-view-all-label]');
          if (countData && countData.results_count) {
            if (viewAllLabel) {
              viewAllLabel.textContent = countData.results_count.trim();
            }
          } else if (viewAllLabel) {
            viewAllLabel.textContent = viewAllLabel.textContent.replace(/\(\s*\)/, '').trim();
          }
        }

        const resultsMarkup = doc.querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderModalResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.modalResults.classList.remove('is-loading');
      });
  }

  renderModalResults(resultsMarkup) {
    this.modalResults.innerHTML = resultsMarkup;
    this.modalResults.classList.remove('is-loading');

    if ($('.dropdown-item[data-currency]').length) {
      if ((window.show_multiple_currencies && typeof Currency != 'undefined' && Currency.currentCurrency != shopCurrency) || window.show_auto_currency) {
        Currency.convertAll(window.shop_currency, $('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
      }
    }
    window.sharedFunctions?.swapHoverVideoProductCard();
    if ($('body').hasClass('product-card-layout-08')) {
      window.sharedFunctions?.productCountdownCard();
    }
  }

  // --- Keep original methods for compatibility but they now do nothing visible ---

  handleResponse(data, limit) {
    let htmlMedia = document.createElement('div');
    htmlMedia.innerHTML = data;
    $(htmlMedia).find(`.product:nth-child(n+${Number(limit) + 1})`).remove();
    return $(htmlMedia).html();
  }

  getQuery() {
    return this.modalInput ? this.modalInput.value.trim() : this.input.value.trim();
  }

  onChange() {}
  onFormSubmit(event) {}
  onFormReset(event) {}
  onFocus() {}
  onFocusOut() {}
  onKeyup(event) {}
  onKeydown(event) {}
  switchOption() {}
  selectOption() {}
  open() {}
  close() {}
  closeResults() {}
  showTrendingAndProducts() {}
  hideTrendingAndProducts() {}

  setLiveRegionLoadingState() {}
  setLiveRegionText() {}
  setLiveRegionResults() {}
  getResultsMaxHeight() { return 0; }

  onDocClick() {}

  onCloseStickySearchClick() {
    this.closeModal();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open');
  }

  updateViewAllLink() {}
  getTotalResults() { return Promise.resolve(0); }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);
